// Abstands-Constraints — engine-frei. Ein Teil hält einen festen Flächenabstand
// zu einem Bezugsteil: meta.constraints = [{ axis, other, gap_mm, side }] am
// ABHÄNGIGEN Teil. side = 1|-1: das abhängige Teil liegt auf der positiven bzw.
// negativen Achsseite des Bezugsteils; gap_mm ist der (vorzeichenfreie) Abstand
// der einander zugewandten AABB-Flächen. Ändert sich das Bezugsteil, wird das
// abhängige Teil über followerUpdates nachgeführt (kaskadierend, zyklusfest).
import { partAabb } from "./snapping"

const AXIS_INDEX = { x: 0, y: 1, z: 2 }
const POS_FIELDS = [ "pos_x", "pos_y", "pos_z" ]

export function constraintsOf(part) {
  return part.meta?.constraints ?? []
}

// Neue Constraint-Liste als meta (leere Liste räumt den Schlüssel weg)
export function withConstraints(part, constraints) {
  const meta = { ...part.meta }
  if (constraints.length > 0) meta.constraints = constraints
  else delete meta.constraints
  return meta
}

// Liest die aktuelle relative Lage und liefert den Constraint-Eintrag mit dem
// IST-Abstand der zugewandten Flächen. Welche Flächen sich gegenüberstehen,
// bestimmt die Seite, auf der das abhängige Teil gerade liegt.
export function constraintFromCurrent(dependent, reference, axis) {
  const i = AXIS_INDEX[axis]
  const a = partAabb(dependent)
  const b = partAabb(reference)
  const side = (a.min[i] + a.max[i]) / 2 >= (b.min[i] + b.max[i]) / 2 ? 1 : -1
  const gap = side === 1 ? a.min[i] - b.max[i] : b.min[i] - a.max[i]
  return { axis, other: reference.client_id, gap_mm: Math.round(gap), side }
}

// Teile, deren Constraints auf clientId zeigen
export function dependentsOf(allParts, clientId) {
  return allParts.filter((part) =>
    constraintsOf(part).some((constraint) => constraint.other === clientId)
  )
}

// Neue Positions-Attrs des abhängigen Teils, sodass alle seine Constraints
// erfüllt sind (pro Achse zählt der erste). Nur geänderte Felder oder null.
export function solveFor(dependent, referenceParts) {
  const own = partAabb(dependent)
  const attrs = {}
  const solvedAxes = new Set()
  for (const { axis, other, gap_mm: gap, side } of constraintsOf(dependent)) {
    const i = AXIS_INDEX[axis]
    if (solvedAxes.has(i)) continue
    const reference = referenceParts.find((part) => part.client_id === other)
    if (!reference) continue
    solvedAxes.add(i)

    const half = (own.max[i] - own.min[i]) / 2
    const ref = partAabb(reference)
    const target = side === 1 ? ref.max[i] + gap + half : ref.min[i] - gap - half
    const value = Math.round(target)
    if (value !== dependent[POS_FIELDS[i]]) attrs[POS_FIELDS[i]] = value
  }
  return Object.keys(attrs).length > 0 ? attrs : null
}

// Kaskadiert mehrere gleichzeitige Ausgangsänderungen (z. B. Anker + gehefteter
// Anhang) und liefert [{ clientId, attrs }] für alle nachzuführenden Teile.
// Max. Tiefe 10, Zyklus-Guard über besuchte Ids — A↔B terminiert.
export function cascadeUpdates(allParts, updates) {
  const working = new Map(allParts.map((part) => [ part.client_id, { ...part } ]))
  const visited = new Set(updates.map(({ clientId }) => clientId))
  const result = []

  let wave = [ ...visited ]
  for (const { clientId, attrs } of updates) {
    const part = working.get(clientId)
    if (part) Object.assign(part, attrs)
  }

  for (let depth = 0; depth < 10 && wave.length > 0; depth++) {
    const next = []
    for (const changedId of wave) {
      for (const dependent of dependentsOf([ ...working.values() ], changedId)) {
        if (visited.has(dependent.client_id)) continue
        visited.add(dependent.client_id)
        const attrs = solveFor(dependent, [ ...working.values() ])
        if (!attrs) continue
        Object.assign(working.get(dependent.client_id), attrs)
        result.push({ clientId: dependent.client_id, attrs })
        next.push(dependent.client_id)
      }
    }
    wave = next
  }
  return result
}

// Einzelne Änderung kaskadieren — Bequemlichkeits-Hülle um cascadeUpdates
export function followerUpdates(allParts, changedClientId, changedAttrs) {
  return cascadeUpdates(allParts, [ { clientId: changedClientId, attrs: changedAttrs } ])
}
